import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize session and user from localStorage or Supabase
  useEffect(() => {
    // Check localStorage for token first (from backend API login)
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('Restoring session from localStorage:', { email: userData.email, role: userData.role });

        // Set token in axios headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

        // Create session object
        const session = {
          access_token: storedToken,
          user: {
            id: userData.id,
            email: userData.email,
            user_metadata: {
              name: userData.name,
              role: userData.role
            }
          }
        };

        setSession(session);
        setUser(userData);
        setLoading(false);
        
        // Optionally verify token is still valid (don't block on this)
        // If verification fails, the next API call will fail and user can re-login
        axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        }).then((response) => {
          // Update user data if backend returns more complete info
          if (response.data) {
            setUser(response.data);
            localStorage.setItem('auth_user', JSON.stringify(response.data));
          }
        }).catch(() => {
          // Token might be expired, but don't clear it yet - let the user try to use it
          // The next API call will fail and they can re-login
          console.warn('Token verification failed, but keeping session. User may need to re-login.');
        });
      } catch (error) {
        console.error('Error restoring session:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        setLoading(false);
      }
    } else {
      // Fallback to Supabase session (for OAuth)
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        if (session) {
          updateUserFromSession(session);
        } else {
          setLoading(false);
        }
      });
    }

    // Listen for Supabase auth changes (for OAuth)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session) {
        await updateUserFromSession(session);
      } else {
        // Only clear if we don't have a stored token
        if (!localStorage.getItem('auth_token')) {
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Update axios headers when session changes
  useEffect(() => {
    if (session?.access_token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [session]);

  const updateUserFromSession = async (session, provider = null) => {
    try {
      // For OAuth logins, sync the user profile first
      if (provider) {
        await axios.post(`${API_URL}/auth/oauth-sync`,
          { provider },
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          }
        );
      }

      // Get user profile from backend (includes role and clientId)
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      setUser(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // If backend call fails, use basic user info from Supabase
      const fallbackUser = {
        id: session.user.id,
        email: session.user.email,
        name: session.user.user_metadata?.name || session.user.email,
        role: session.user.user_metadata?.role || 'client'
      };
      setUser(fallbackUser);
      setLoading(false);
      return fallbackUser;
    }
  };

  const login = async (email, password) => {
    try {
      // Use backend API for login (handles Supabase Auth and returns token)
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });

      const { token, user: userData } = response.data;

      if (!token || !userData) {
        return { success: false, error: 'Login failed. Please try again.' };
      }

      // Set the token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Store token and user in localStorage for persistence
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_user', JSON.stringify(userData));

      // Also set the session in Supabase for compatibility
      // We'll create a session object manually since we have the token
      const session = {
        access_token: token,
        user: {
          id: userData.id,
          email: userData.email,
          user_metadata: {
            name: userData.name,
            role: userData.role
          }
        }
      };

      // Update state
      setSession(session);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Login failed. Please try again.'
      };
    }
  };

  const register = async (email, password, name, invitationCode = null) => {
    try {
      // Register via backend API (handles user profile creation and client linking)
      const response = await axios.post(`${API_URL}/auth/register`, {
        email,
        password,
        name,
        invitationCode
      });

      const { token, user: userData } = response.data;

      // Sign in with Supabase to get session
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Error signing in after registration:', error);
        // Still return success since user was created
        return { success: true, user: userData };
      }

      if (data.session) {
        await updateUserFromSession(data.session);
        return { success: true, user: userData };
      }

      return { success: true, user: userData };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Registration failed. Please try again.'
      };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?provider=google`
        }
      });

      if (error) throw error;

      // OAuth redirect will happen, so we don't need to return anything
      return { success: true };
    } catch (error) {
      console.error('Google login failed:', error);
      return {
        success: false,
        error: error.message || 'Google login failed. Please try again.'
      };
    }
  };

  const loginWithGitHub = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?provider=github`
        }
      });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('GitHub login failed:', error);
      return {
        success: false,
        error: error.message || 'GitHub login failed. Please try again.'
      };
    }
  };

  const sendMagicLink = async (email) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Magic link failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to send magic link. Please try again.'
      };
    }
  };

  const logout = async () => {
    try {
      // Clear localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      
      // Clear Supabase session if exists
      await supabase.auth.signOut();
      
      // Clear state
      setUser(null);
      setSession(null);
      delete axios.defaults.headers.common['Authorization'];
    } catch (error) {
      console.error('Logout error:', error);
      // Clear state anyway
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      setUser(null);
      setSession(null);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const value = {
    user,
    token: session?.access_token || localStorage.getItem('auth_token') || null,
    loading,
    login,
    register,
    loginWithGoogle,
    loginWithGitHub,
    sendMagicLink,
    logout,
    isAuthenticated: !!user && (!!session || !!localStorage.getItem('auth_token')),
    isCoach: user?.role === 'coach',
    isClient: user?.role === 'client'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
